/**
 * Month view row sizing for delivery calendar.
 *
 * We intentionally size each month week-row to the rendered event content
 * so all cards are visible without per-week internal scrollbars.
 */
import { useLayoutEffect } from 'react';
import type { CalendarEvent, CalendarView } from '../types/calendar';

export function useCalendarRowHeights(
  _currentDate: Date,
  _events: CalendarEvent[],
  _expandedEventIds: string[],
  _currentView: CalendarView
): void {
  useLayoutEffect(() => {
    if (_currentView !== 'month') {
      return;
    }

    const applyRowHeights = () => {
      const monthView = document.querySelector<HTMLElement>('.calendar-container .rbc-month-view');
      if (!monthView) {
        return;
      }

      const rows = monthView.querySelectorAll<HTMLElement>('.rbc-month-row');
      rows.forEach((row) => {
        const scrollContainer = row.querySelector<HTMLElement>(
          '.rbc-row-content-scrollable .rbc-row-content-scroll-container'
        );
        const rowContent = row.querySelector<HTMLElement>('.rbc-row-content');
        const measurementTarget = scrollContainer ?? rowContent;
        if (!measurementTarget) {
          return;
        }

        // Add a small buffer for borders and subpixel rounding.
        const requiredHeight = Math.ceil(measurementTarget.scrollHeight) + 2;
        const finalHeight = Math.max(requiredHeight, 110);

        row.style.height = `${finalHeight}px`;
        row.style.minHeight = `${finalHeight}px`;
      });
    };

    let rafId = window.requestAnimationFrame(() => {
      rafId = window.requestAnimationFrame(applyRowHeights);
    });

    const handleResize = () => {
      applyRowHeights();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener('resize', handleResize);
    };
  }, [_currentDate, _events, _expandedEventIds, _currentView]);
}
